// src/utils/downloadFile.js
// Cross-platform file download that works on iOS Safari.
//
// iOS Safari problems:
//   - Ignores the `download` attribute on <a> tags
//   - Suppresses window.open() calls not directly from a synchronous user gesture
//   - Blocks fetch() blob approach when CORS headers are missing on the file server
//
// Solution: detect iOS and use window.location.href directly, which iOS Safari
// handles correctly — it either shows the file in its viewer or downloads it.
// On desktop browsers use the blob approach for a proper inline download.

const isIOS = () =>
  typeof navigator !== 'undefined' &&
  (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.maxTouchPoints > 1 && /Mac/.test(navigator.userAgent)));

export async function downloadFile(url, filename) {
  // iOS Safari: navigate directly. Safari will download or preview the file.
  // This must stay synchronous from the user gesture — no await before this.
  if (isIOS()) {
    window.location.href = url;
    return;
  }

  // Desktop: fetch as blob for a proper inline download without leaving the page.
  try {
    const response = await fetch(url, { mode: 'cors' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename || 'download';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();

    setTimeout(() => {
      URL.revokeObjectURL(objectUrl);
      document.body.removeChild(a);
    }, 150);
  } catch (err) {
    console.warn('[downloadFile] Blob fetch failed, falling back to direct link:', err);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'download';
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => document.body.removeChild(a), 150);
  }
}
